import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
    ArrowLeft, Download, ChevronRight, ChevronDown, ChevronUp, Gamepad2, Music, Grid3X3,
    Smile, Play, Pause, Volume2, VolumeX, Image as ImageIcon, RotateCcw,
    Type, Palette, Wand2, Loader2, Video, Bold, Italic, Save, Check, Youtube
} from 'lucide-react';
import { ProjectService } from '../services/ProjectService';
import { YouTubeUploadModal } from '../components/YouTubeUploadModal';
import MemePickerModal from '../components/MemePickerModal';
import MemePropertiesEditor from '../components/MemePropertiesEditor';
import SfxPickerModal from '../components/SfxPickerModal';
import './RantEditorPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Font options - using fonts that are available on most systems
// Font options - using fonts that are available locally in backend/downloads/fonts
// Font options - using fonts that are available locally in backend/downloads/fonts
const FONT_OPTIONS = [
    { id: 'anton', label: 'Anton', family: '"Anton", Impact, sans-serif' },
    { id: 'archivo-black', label: 'Archivo Black', family: '"Archivo Black", Impact, sans-serif' },
    { id: 'bebas-neue', label: 'Bebas Neue', family: '"Bebas Neue", Impact, sans-serif' },
    { id: 'bogle', label: 'Bogle', family: '"Bogle", sans-serif' },
    { id: 'impact', label: 'Impact', family: '"Impact", sans-serif' },
    { id: 'lato', label: 'Lato', family: '"Lato", sans-serif' },
    { id: 'montserrat', label: 'Montserrat', family: '"Montserrat", sans-serif' },
    { id: 'oswald', label: 'Oswald', family: '"Oswald", sans-serif' },
];

const CUSTOM_FONTS_CONFIG = [
    { family: 'Lato', prefix: 'lato', weights: [700, 900] },
    { family: 'Archivo Black', prefix: 'archivo-black', weights: [700, 900] },
    { family: 'Impact', prefix: 'impact', weights: [400, 700, 900] },
    { family: 'Montserrat', prefix: 'montserrat', weights: [400, 700, 900] },
    { family: 'Anton', prefix: 'anton', weights: [400, 700] },
    { family: 'Oswald', prefix: 'oswald', weights: [400, 700] },
    { family: 'Bebas Neue', prefix: 'bebas-neue', weights: [400, 700] },
    { family: 'Bogle', prefix: 'bogle', weights: [400, 700, 900] },
];

// Emoji picker data
const EMOJI_OPTIONS = [
    '😭', '🤣', '😤', '🙄', '💀', '😱', '🔥', '💯',
    '😂', '😩', '🤯', '👀', '🙏', '😅', '🥴', '😬',
    '👏', '🤡', '💔', '⚡', '🎯', '🧠', '😈', '🤮'
];

// Color options for text
const COLOR_OPTIONS = [
    '#FFFFFF', '#FFEB3B', '#00BCD4', '#E91E63',
    '#4CAF50', '#FF9800', '#F44336', '#9C27B0',
    '#2196F3', '#00E676', '#FF5722', '#673AB7'
];

// Voice changer presets
const VOICE_EFFECTS = [
    { id: 'none', label: 'None (Original)' },
    { id: 'high_pitch', label: 'High Pitch' },
    { id: 'low_pitch', label: 'Low Pitch' },
    { id: 'echo', label: 'Echo' },
    { id: 'robot', label: 'Robot' },
];

// Available voices (ElevenLabs) with preview URLs
const VOICES = [
    // Male voices
    { id: 'adam', label: 'Adam', description: 'Deep, authoritative male voice', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/pNInz6obpgDQGcFmaJgB/d6905d7a-dd26-4187-bfff-1bd3a5ea7cac.mp3' },
    { id: 'brian', label: 'Brian', description: 'Deep male voice, great for narration', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/nPczCjzI2devNBz1zQrb/2dd3e72c-4fd3-42f1-93ea-abc5d4e5aa1d.mp3' },
    { id: 'liam', label: 'Liam', description: 'Young male voice, natural narration', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/TX3LPaxmHKxFdv7VOQHJ/63148076-6363-42db-aea8-31424308b92c.mp3' },
    // Female voices
    { id: 'jessica', label: 'Jessica', description: 'Young, expressive female voice', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/cgSgspJ2msm6clMCkdW9/56a97bf8-b69b-448f-846c-c3a11683d45a.mp3' },
    { id: 'matilda', label: 'Matilda', description: 'Warm female voice, audiobook style', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/XrExE9yKIg1WjnnlVkGX/b930e18d-6b4d-466e-bab2-0ae97c6d8535.mp3' },
    { id: 'sarah', label: 'Sarah', description: 'Soft, gentle female voice', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/EXAVITQu4vr4xnSDxMaL/01a3e33c-6e99-4ee7-8543-ff2216a32186.mp3' },
];

// Shared audio element for voice previews (only one plays at a time)
let previewAudio = null;

// --- Voice Preview Button Component ---
const VoicePreviewButton = ({ voiceId, voiceList }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    // Use passed voiceList or fallback to static VOICES
    const voice = (voiceList || VOICES).find(v => v.id === voiceId);

    const handleClick = () => {
        if (!voice?.previewUrl) return;

        // Stop any currently playing preview
        if (previewAudio) {
            previewAudio.pause();
            previewAudio.currentTime = 0;
        }

        if (isPlaying) {
            setIsPlaying(false);
            return;
        }

        // Play new preview
        previewAudio = new Audio(voice.previewUrl);
        previewAudio.onended = () => setIsPlaying(false);
        previewAudio.onerror = () => setIsPlaying(false);
        previewAudio.play().catch(() => setIsPlaying(false));
        setIsPlaying(true);
    };

    if (!voice?.previewUrl) return null; // Hide if no preview

    return (
        <button
            className="voice-preview-btn"
            onClick={handleClick}
            title={isPlaying ? "Stop preview" : `Preview ${voice?.label || 'voice'}`}
            type="button"
        >
            {isPlaying ? <Volume2 size={16} className="playing" /> : <Play size={14} />}
        </button>
    );
};

// --- Word Token Component for editing ---
const WordToken = ({
    word,
    onColorChange,
    onEmojiChange,
    onLineBreakToggle,
    onSfxChange,
    onSfxClick, // Opens SFX picker modal
    onVoiceEffectChange,
    sfxLibrary = []
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [menuType, setMenuType] = useState(null);
    const tokenRef = useRef(null);
    const audioRef = useRef(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (tokenRef.current && !tokenRef.current.contains(e.target)) {
                setShowMenu(false);
                setMenuType(null);
            }
        };
        if (showMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showMenu]);

    // Calculate menu position when showing
    // Calculate menu position when showing
    const handleTokenClick = () => {
        if (!showMenu && tokenRef.current) {
            const rect = tokenRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            // Estimates for mobile popup dimensions
            const menuHeightEstimate = 320;
            const menuWidthEstimate = 280;
            const safeMargin = 16; // Edge padding

            // Vertical Decision: Flip up if close to bottom
            const spaceBelow = viewportHeight - rect.bottom;
            // If space below is tight (<320px) AND we have more space above, flip up.
            // Or if we are in the bottom 35% of screen.
            const showAbove = spaceBelow < menuHeightEstimate && rect.top > menuHeightEstimate;

            // Horizontal Clamp: Ensure 50% center point is "safe" relative to edges
            let left = rect.left + rect.width / 2;
            left = Math.max((menuWidthEstimate / 2) + safeMargin, left);
            left = Math.min(viewportWidth - (menuWidthEstimate / 2) - safeMargin, left);

            setMenuPosition({
                top: showAbove ? undefined : rect.bottom + 8,
                bottom: showAbove ? (viewportHeight - rect.top + 8) : undefined,
                left: left
            });
        }
        setShowMenu(!showMenu);
        setMenuType(null);
    };

    // Play SFX preview
    const playSfxPreview = (sfxUrl) => {
        if (audioRef.current) {
            audioRef.current.src = sfxUrl;
            audioRef.current.play().catch(() => { });
        }
    };

    // Check if word has attachments
    const hasSfx = !!word.sfx;
    const hasVoiceEffect = word.voiceEffect && (word.voiceEffect.pitch !== 'normal' || word.voiceEffect.emphasis);

    return (
        <div ref={tokenRef} className="word-token-wrapper">
            <audio ref={audioRef} style={{ display: 'none' }} />
            <div
                className={`word-token ${showMenu ? 'active' : ''} ${hasSfx || hasVoiceEffect ? 'has-attachments' : ''}`}
                onClick={handleTokenClick}
            >
                <span className="word-text" style={{ color: word.color || '#FFFFFF' }}>
                    {word.text}
                </span>
                {word.emoji && <span className="word-emoji">{word.emoji}</span>}
                {word.lineBreak && <span className="line-break-indicator">↵</span>}

                {/* Attachment indicators */}
                <div className="word-indicators">
                    {hasSfx && <span className="indicator sfx-indicator" title="Has SFX">🔊</span>}
                    {hasVoiceEffect && <span className="indicator voice-indicator" title="Voice effect">🎤</span>}
                </div>
            </div>

            {showMenu && (
                <div
                    className="word-menu"
                    style={{
                        top: menuPosition.top,
                        bottom: menuPosition.bottom,
                        left: menuPosition.left,
                        transform: 'translateX(-50%)'
                    }}
                >
                    {menuType === null && (
                        <div className="menu-buttons">
                            <button className="menu-btn" onClick={(e) => { e.stopPropagation(); setMenuType('color'); }}>
                                <Palette size={14} /> Color
                            </button>
                            <button className="menu-btn" onClick={(e) => { e.stopPropagation(); setMenuType('emoji'); }}>
                                <Smile size={14} /> Emoji
                            </button>
                            <button className="menu-btn" onClick={(e) => { e.stopPropagation(); onSfxClick?.(); setShowMenu(false); }}>
                                <Volume2 size={14} /> SFX
                            </button>
                            <button className="menu-btn" onClick={(e) => { e.stopPropagation(); setMenuType('voice'); }}>
                                🎤 Voice
                            </button>
                            <button
                                className={`menu-btn ${word.lineBreak ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); onLineBreakToggle(); setShowMenu(false); }}
                            >
                                ↵ Break
                            </button>
                        </div>
                    )}
                    {menuType === 'color' && (
                        <div className="color-menu">
                            <button className="back-btn-small" onClick={(e) => { e.stopPropagation(); setMenuType(null); }}>← Back</button>
                            <div className="color-grid">
                                {COLOR_OPTIONS.map(c => (
                                    <button
                                        key={c}
                                        className={`color-btn ${word.color === c ? 'selected' : ''}`}
                                        style={{ backgroundColor: c }}
                                        onClick={(e) => { e.stopPropagation(); onColorChange(c); setShowMenu(false); }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {menuType === 'emoji' && (
                        <div className="emoji-menu">
                            <button className="back-btn-small" onClick={(e) => { e.stopPropagation(); setMenuType(null); }}>← Back</button>
                            {word.emoji && (
                                <button className="remove-emoji-btn" onClick={(e) => { e.stopPropagation(); onEmojiChange(null); setShowMenu(false); }}>
                                    Remove emoji
                                </button>
                            )}
                            <div className="emoji-grid">
                                {EMOJI_OPTIONS.map(emoji => (
                                    <button
                                        key={emoji}
                                        className={`emoji-btn ${word.emoji === emoji ? 'selected' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); onEmojiChange(emoji); setShowMenu(false); }}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {menuType === 'sfx' && (
                        <div className="sfx-menu">
                            <button className="back-btn-small" onClick={(e) => { e.stopPropagation(); setMenuType(null); }}>← Back</button>
                            {word.sfx && (
                                <button
                                    className="remove-sfx-btn"
                                    onClick={(e) => { e.stopPropagation(); onSfxChange?.(null); setShowMenu(false); }}
                                >
                                    Remove SFX
                                </button>
                            )}
                            <div className="sfx-list">
                                {sfxLibrary.length === 0 ? (
                                    <p className="no-sfx">No SFX available</p>
                                ) : (
                                    sfxLibrary.map(sfx => (
                                        <div
                                            key={sfx.id}
                                            className={`sfx-item ${word.sfx?.id === sfx.id ? 'selected' : ''}`}
                                        >
                                            <button
                                                className="sfx-play-btn"
                                                onClick={(e) => { e.stopPropagation(); playSfxPreview(sfx.url); }}
                                            >
                                                ▶️
                                            </button>
                                            <span
                                                className="sfx-label"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSfxChange?.({ id: sfx.id, url: sfx.url, volume: 1.0 });
                                                    setShowMenu(false);
                                                }}
                                            >
                                                {sfx.label}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                    {menuType === 'voice' && (
                        <div className="voice-menu">
                            <button className="back-btn-small" onClick={(e) => { e.stopPropagation(); setMenuType(null); }}>← Back</button>

                            <div className="coming-soon-notice">
                                <span className="coming-soon-icon">🎤</span>
                                <h4>Per-Word Voice Effects</h4>
                                <p>Change pitch for individual words</p>
                                <span className="coming-soon-badge">Coming Soon</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Collapsible Section ---
// --- Collapsible Section ---
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className={`collapsible-section ${isOpen ? 'open' : ''}`}>
            <div className="section-header clickable" onClick={() => setIsOpen(!isOpen)}>
                {Icon && <Icon size={18} />}
                <h3>{title}</h3>
                <div style={{ flex: 1 }} />
                <div className="collapse-icon">
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </div>
            {isOpen && <div className="section-content">{children}</div>}
        </div>
    );
};

// --- Main Editor Component ---
export const RantEditorPage = ({
    session,
    audioData,
    sentences: initialSentences,
    onBack,
    title = "Untitled Rant",
    initialProject = null,
    projectId: initialProjectId = null,
    refreshUsage
}) => {
    // Project state
    const [projectId, setProjectId] = useState(initialProjectId);
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved
    const [lastRenderedPath, setLastRenderedPath] = useState(null); // Supabase path of last rendered video
    const [lastDownloadUrl, setLastDownloadUrl] = useState(null); // Download URL of last rendered video
    const [currentJobId, setCurrentJobId] = useState(audioData?.job_id); // Store job ID to associate with storage

    // Viewport tracking for responsive preview scaling
    // Viewport tracking for responsive preview scaling
    const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const [previewWidth, setPreviewWidth] = useState(400); // Default estimate
    const canvasWrapperRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    // Panel tabs
    const [activePanel, setActivePanel] = useState('editor');

    // Bottom Sheet Expansion State (Mobile)
    const [isEditorExpanded, setIsEditorExpanded] = useState(false);
    const touchStartY = useRef(0);

    const handleSheetTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
    const handleSheetTouchEnd = (e) => {
        const diff = touchStartY.current - e.changedTouches[0].clientY;
        if (diff > 50) setIsEditorExpanded(true); // Swipe Up
        if (diff < -50) setIsEditorExpanded(false); // Swipe Down
    };

    // Observer for preview container width
    useEffect(() => {
        if (!canvasWrapperRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.contentRect) {
                    setPreviewWidth(entry.contentRect.width);
                }
            }
        });
        observer.observe(canvasWrapperRef.current);
        return () => observer.disconnect();
    }, [activePanel, isEditorExpanded]);

    // Content - sentences with words
    const [content, setContent] = useState([]);

    // Caption Settings (matching screenshot)
    const [captionSettings, setCaptionSettings] = useState({
        font: 'bebas-neue',
        fontSize: 75,
        upperCase: false,
        fontWeight: 'extra-bold', // Default to thicker weight for impact
        italic: false,
        textColor: '#FFFFFF',
        shadowEnabled: true,
        shadowColor: '#000000',
        shadowSize: 5,
        backgroundEnabled: true,
        backgroundColor: 'rgba(0,0,0,0.5)',
        paddingTop: 10, // percentage from top
        maxWidth: 90,   // % of width
    });

    // Meme Positioning
    const [memePlacement, setMemePlacement] = useState({ top: 50, isCentered: true, maxHeightPercent: 85 });
    const captionRef = useRef(null);

    // Voice effect
    const [voiceEffect, setVoiceEffect] = useState('none');
    const [applyingVoiceEffect, setApplyingVoiceEffect] = useState(false);

    // Voice list state
    const [availableVoices, setAvailableVoices] = useState(VOICES);
    const [voicesLoading, setVoicesLoading] = useState(false);

    // New voice selection (for regenerating audio with a different voice)
    const [newVoice, setNewVoice] = useState('adam');
    const [applyingNewVoice, setApplyingNewVoice] = useState(false);

    // Assets
    const [gameplayLibrary, setGameplayLibrary] = useState([]);
    const [sfxLibrary, setSfxLibrary] = useState([]);
    const [memesLibrary, setMemesLibrary] = useState([]);
    const [selectedGameplay, setSelectedGameplay] = useState(null);
    const [assetsLoading, setAssetsLoading] = useState(true);

    // Restore state from project if available
    useEffect(() => {
        if (initialProject && initialProject.data) {
            const data = initialProject.data;

            // Restore Settings
            if (data.captionSettings) setCaptionSettings(data.captionSettings);
            if (data.voiceEffect) setVoiceEffect(data.voiceEffect);
            if (data.job_id) setCurrentJobId(data.job_id);
            if (data.supabase_path) setLastRenderedPath(data.supabase_path);
            if (data.video_url) setLastDownloadUrl(data.video_url);

            // Restore Selected Gameplay (need to find in library or set directly)
            if (data.selectedGameplay) {
                setSelectedGameplay(data.selectedGameplay);
            }

            // Restore Content (Words/Sentences overrides initialSentences if present)
            if (data.content) {
                // We use a timeout to let the initial parsing (other useEffect) happen first, 
                // then overwrite it with saved content state which might include edits.
                setTimeout(() => setContent(data.content), 100);
            }
        }
    }, [initialProject]);

    // Update currentJobId if audioData changes (e.g. fresh generation)
    useEffect(() => {
        if (audioData?.job_id) {
            setCurrentJobId(audioData.job_id);
        }
    }, [audioData]);


    const handleSaveProject = async () => {
        if (!session?.access_token) {
            alert("Please log in to save projects");
            return;
        }

        setSaveStatus('saving');
        try {
            const projectData = {
                // Creator State (for restoration if we go back)
                topic: title,
                sentences: initialSentences, // Original sentences

                // Editor State
                job_id: currentJobId,
                content, // Edited content (with words, colors, emojis)
                captionSettings,
                voiceEffect,
                selectedGameplay,
                supabase_path: lastRenderedPath, // Include for storage tracking
                updatedAt: new Date().toISOString()
            };

            const projectName = title || `Rant: ${initialSentences?.[0]?.text?.substring(0, 20)}...`;

            // Use gameplay thumbnail if available
            const thumbnail = selectedGameplay?.thumbnail || null;

            if (projectId) {
                // Update
                await ProjectService.updateProject(session.access_token, projectId, {
                    name: projectName,
                    data: projectData,
                    thumbnail_url: thumbnail,
                    type: 'rant'
                });
            } else {
                // Create
                const newProject = await ProjectService.createProject(
                    session.access_token,
                    'rant',
                    projectName,
                    projectData
                );
                setProjectId(newProject.id);

                // Update URL 
                const newUrl = new URL(window.location);
                newUrl.searchParams.set('project_id', newProject.id);
                window.history.pushState({}, '', newUrl);
            }

            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);

            return projectId; // Return ID for auto-save

        } catch (err) {
            console.error(err);
            // Only alert on manual save
            if (saveStatus !== 'idle') {
                alert("Failed to save project");
                setSaveStatus('idle');
            }
        }
    };


    // Preview & Playback
    const [isPlaying, setIsPlaying] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioLoaded, setAudioLoaded] = useState(false);
    const audioRef = useRef(null);
    const gameplayVideoRef = useRef(null);

    // Timestamps - now tracking sentences and words
    const [sentenceTimestamps, setSentenceTimestamps] = useState([]);
    const [wordTimestamps, setWordTimestamps] = useState([]); // Word-level for SFX triggering
    const [fullTimestampsData, setFullTimestampsData] = useState(null); // Full data for render request
    const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);

    // SFX playback during preview
    const sfxAudioRef = useRef(null);
    const playedSfxRef = useRef(new Set()); // Track which word SFX have played to avoid retriggering

    // Export
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(null);

    // Meme/SFX picker state
    const [memePickerOpen, setMemePickerOpen] = useState(false);
    const [selectedSentenceForMeme, setSelectedSentenceForMeme] = useState(null);
    const [memePropertiesOpen, setMemePropertiesOpen] = useState(null); // { sentenceIdx: number }

    // SFX picker state - tracks which word is being edited { sentenceIdx, wordIdx, currentSfx }
    const [sfxPickerOpen, setSfxPickerOpen] = useState(null);

    // Cache buster for audio URL to force reload after effects
    const [audioCacheBuster, setAudioCacheBuster] = useState(0);

    // YouTube Upload Modal
    const [showYoutubeModal, setShowYoutubeModal] = useState(false);

    // Audio URL with cache busting
    const audioUrl = currentJobId ? `${API_BASE}/api/rant/audio/${currentJobId}?t=${audioCacheBuster}` : null;

    // Parse sentences on mount
    useEffect(() => {
        if (initialSentences && initialSentences.length > 0) {
            const parsed = initialSentences.map((sentence, idx) => ({
                index: idx,
                text: sentence.text,
                voice: sentence.voice,
                // NEW: Meme/hook attached to this sentence
                meme: null, // { id, url, source, duration, offsetTop, scale }
                words: sentence.text.split(/\s+/).filter(w => w.length > 0).map(word => ({
                    text: word,
                    color: '#FFFFFF',
                    emoji: null,
                    lineBreak: false,
                    // NEW: SFX attached to this word
                    sfx: null, // { id, url, volume }
                    // NEW: Voice effect for this word
                    voiceEffect: null // { pitch: 'normal'|'high'|'low', emphasis: boolean }
                }))
            }));
            setContent(parsed);
        }
    }, [initialSentences]);

    // Fetch timestamps and build sentence timing
    useEffect(() => {
        const fetchTimestamps = async () => {
            if (audioData?.job_id) {
                try {
                    const res = await fetch(`${API_BASE}/api/rant/timestamps/${audioData.job_id}`);
                    if (res.ok) {
                        const data = await res.json();

                        // Store word-level timestamps for SFX triggering
                        if (data.word_timestamps) {
                            setWordTimestamps(data.word_timestamps);
                            console.log("Word timestamps loaded:", data.word_timestamps.length, "words");
                        }

                        // Store full timestamps data for render request
                        setFullTimestampsData(data);

                        // Build sentence-level timestamps from word data
                        const sentences = data.sentences || [];
                        let cumTime = 0;
                        const sentTimes = sentences.map((s, i) => {
                            const start = cumTime;
                            const end = cumTime + (s.duration || 2);
                            cumTime = end;
                            return { index: i, start, end, text: s.text };
                        });

                        setSentenceTimestamps(sentTimes);
                        console.log("Sentence timestamps:", sentTimes);
                    }
                } catch (err) {
                    console.error("Failed to fetch timestamps:", err);
                }
            }
        };
        fetchTimestamps();
    }, [audioData]);

    // Helper to get tempo multiplier for current voice effect
    const getTempoMultiplier = () => {
        if (voiceEffect === 'high_pitch' || voiceEffect === 'high') return 1.15; // Faster
        if (voiceEffect === 'low_pitch' || voiceEffect === 'low') return 0.88;  // Slower
        return 1.0;
    };

    // Update current sentence based on playback time
    useEffect(() => {
        if (sentenceTimestamps.length === 0) return;

        const tempoFactor = getTempoMultiplier();

        const current = sentenceTimestamps.findIndex(st => {
            // Scale timestamps by tempo factor to match audio speed
            // If tempo > 1 (faster), timestamps should be smaller (happen earlier) -> divide by tempo
            const scaledStart = st.start / tempoFactor;
            const scaledEnd = st.end / tempoFactor;
            return currentTime >= scaledStart && currentTime < scaledEnd;
        });

        if (current !== -1 && current !== currentSentenceIndex) {
            setCurrentSentenceIndex(current);
        }
    }, [currentTime, sentenceTimestamps, currentSentenceIndex, voiceEffect]);

    // SFX playback during preview - trigger SFX when word with SFX is being spoken
    useEffect(() => {
        if (!isPlaying || wordTimestamps.length === 0 || content.length === 0) return;

        // Build a flat list of words with their timestamps and SFX
        const wordsWithTiming = [];
        let wordIdx = 0;
        for (let sIdx = 0; sIdx < content.length; sIdx++) {
            for (let wIdx = 0; wIdx < content[sIdx].words.length; wIdx++) {
                const word = content[sIdx].words[wIdx];
                const timing = wordTimestamps[wordIdx];
                if (timing && word.sfx) {
                    wordsWithTiming.push({
                        sIdx,
                        wIdx,
                        word,
                        start: timing.start,
                        end: timing.end,
                        sfxKey: `${sIdx}-${wIdx}`
                    });
                }
                wordIdx++;
            }
        }

        // Check if any SFX should play now (trigger 0.1s early to account for loading)
        const tempoFactor = getTempoMultiplier();

        for (const item of wordsWithTiming) {
            // Scale timestamps by tempo factor
            // If tempo > 1 (faster), timestamps should be smaller (happen earlier) -> divide by tempo
            const scaledStart = item.start / tempoFactor;
            const scaledEnd = item.end / tempoFactor;

            const triggerTime = scaledStart - 0.1; // Trigger slightly early

            if (currentTime >= triggerTime && currentTime < scaledEnd && !playedSfxRef.current.has(item.sfxKey)) {
                playedSfxRef.current.add(item.sfxKey);
                // Play the SFX
                if (sfxAudioRef.current) {
                    sfxAudioRef.current.src = item.word.sfx.url;
                    sfxAudioRef.current.volume = item.word.sfx.volume || 1.0;
                    sfxAudioRef.current.load(); // Preload
                    sfxAudioRef.current.play().catch(e => console.log("SFX play failed:", e));
                    console.log(`Playing SFX for word "${item.word.text}" at ${currentTime}s (word starts at ${scaledStart.toFixed(2)}s)`);
                }
            }
        }
    }, [currentTime, isPlaying, wordTimestamps, content, voiceEffect]);

    // Reset played SFX when playback stops or seeks
    useEffect(() => {
        if (!isPlaying) {
            playedSfxRef.current.clear();
        }
    }, [isPlaying]);

    // Fetch assets
    useEffect(() => {
        const fetchAssets = async () => {
            setAssetsLoading(true);
            try {
                const [gameplayRes, sfxRes, memesRes] = await Promise.all([
                    fetch(`${API_BASE}/api/assets/gameplay`),
                    fetch(`${API_BASE}/api/assets/sfx`),
                    fetch(`${API_BASE}/api/assets/memes`)
                ]);

                setGameplayLibrary((await gameplayRes.json()).gameplay || []);
                setSfxLibrary((await sfxRes.json()).sfx || []);
                setMemesLibrary((await memesRes.json()).memes || []);
            } catch (err) {
                console.error("Failed to fetch assets:", err);
            } finally {
                setAssetsLoading(false);
            }
        };
        fetchAssets();
    }, []);

    // Fetch voices
    useEffect(() => {
        const fetchVoices = async () => {
            setVoicesLoading(true);
            try {
                const headers = {};
                if (session?.access_token) {
                    headers['Authorization'] = `Bearer ${session.access_token}`;
                }

                const res = await fetch(`${API_BASE}/api/rant/voices`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    if (data.voices) {
                        setAvailableVoices(data.voices);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch voices:", err);
            } finally {
                setVoicesLoading(false);
            }
        };
        fetchVoices();
    }, [session?.access_token]);

    // Load custom fonts from backend - DISABLED due to 404s
    /*
    useEffect(() => {
        const styleId = 'custom-fonts-loader';
        if (document.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;

        let css = '';
        CUSTOM_FONTS_CONFIG.forEach(font => {
            font.weights.forEach(weight => {
                const filename = `${font.prefix}-${weight}.ttf`;
                css += `
                @font-face {
                    font-family: '${font.family}';
                    src: url('${API_BASE}/api/assets/file/fonts/${filename}') format('truetype');
                    font-weight: ${weight};
                    font-style: normal;
                }
                `;
            });
        });

        style.textContent = css;
        document.head.appendChild(style);
    }, []);
    */

    // Update word in content
    const updateWord = (sentenceIndex, wordIndex, updates) => {
        setContent(prev => {
            const newContent = [...prev];
            const sentence = { ...newContent[sentenceIndex] };
            const words = [...sentence.words];
            words[wordIndex] = { ...words[wordIndex], ...updates };
            sentence.words = words;
            newContent[sentenceIndex] = sentence;
            return newContent;
        });
    };

    // Update sentence (for meme attachment)
    const updateSentence = (sentenceIndex, updates) => {
        setContent(prev => {
            const newContent = [...prev];
            newContent[sentenceIndex] = { ...newContent[sentenceIndex], ...updates };
            return newContent;
        });
    };

    // Handle Add Hook button click
    const handleAddHook = (sentenceIndex) => {
        setSelectedSentenceForMeme(sentenceIndex);
        setMemePickerOpen(true);
    };

    // Handle meme selection from picker
    const handleMemeSelected = (memeData) => {
        if (selectedSentenceForMeme !== null) {
            // Get sentence duration from timestamps if available
            const sentenceTs = sentenceTimestamps[selectedSentenceForMeme];
            const sentenceDuration = sentenceTs ? (sentenceTs.end - sentenceTs.start) * 1000 : 3000;

            updateSentence(selectedSentenceForMeme, {
                meme: {
                    id: memeData.id || null,
                    url: memeData.url,
                    source: memeData.source || 'library',
                    // Timeline range: default to full sentence duration
                    startTime: 0,
                    endTime: sentenceDuration,
                    offsetTop: 0, // Default to 0 so dynamic positioning works correctly (was -15)
                    scale: 0.75  // Start at 75% size (25% smaller than full)
                }
            });
        }
        setMemePickerOpen(false);
        setSelectedSentenceForMeme(null);
    };

    // Remove meme from sentence
    const handleRemoveMeme = (sentenceIndex) => {
        updateSentence(sentenceIndex, { meme: null });
    };

    // Audio controls
    const togglePlayback = async () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            // Pause
            audioRef.current.pause();
            if (gameplayVideoRef.current) gameplayVideoRef.current.pause();
            setIsPlaying(false);
            setIsBuffering(false);
        } else {
            // Play with sync
            setIsBuffering(true);
            try {
                const playPromises = [];
                if (audioRef.current) playPromises.push(audioRef.current.play());
                if (gameplayVideoRef.current) playPromises.push(gameplayVideoRef.current.play());

                await Promise.all(playPromises);
                setIsPlaying(true);
            } catch (err) {
                // If the error is an AbortError, it means playback was interrupted (e.g. by pause)
                // We should ensure state reflects reality, but not spam console for normal interruptions
                if (err.name === 'AbortError') {
                    console.warn("Playback interrupted (AbortError)");
                    // Ensure we are in paused state
                    setIsPlaying(false);
                } else {
                    console.error("Playback failed:", err);
                    setIsPlaying(false);
                }
            } finally {
                setIsBuffering(false);
            }
        }
    };

    // Apply voice effect
    const handleApplyVoiceEffect = async () => {
        if (!currentJobId || voiceEffect === 'none') return;

        setApplyingVoiceEffect(true);
        try {
            const response = await fetch(`${API_BASE}/api/rant/apply-voice-effect`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`
                },
                body: JSON.stringify({
                    job_id: currentJobId,
                    effect: voiceEffect
                })
            });

            const result = await response.json();

            // Handle expired audio session
            if (result.code === 'AUDIO_EXPIRED') {
                alert('Your audio session has expired. Please go back and regenerate the voice to apply effects.');
                return;
            }

            if (!response.ok) throw new Error(result.error || 'Failed to apply effect');

            // Reload audio with new effect applied
            setAudioCacheBuster(prev => prev + 1);
            if (audioRef.current) {
                audioRef.current.load();
                setAudioLoaded(false);
            }

            alert(`Voice effect "${voiceEffect}" applied successfully!`);
        } catch (err) {
            alert(`Failed to apply voice effect: ${err.message}`);
        } finally {
            setApplyingVoiceEffect(false);
        }
    };

    // Handle export
    const handleExport = async () => {
        if (!currentJobId || !selectedGameplay) {
            alert("Please select a background gameplay video first");
            return;
        }

        setIsExporting(true);
        setExportProgress("Processing");

        // Force switch to settings panel to ensure DOM elements are rendered for measurement
        if (activePanel !== 'settings') {
            setActivePanel('settings');
            // Give React a moment to render the DOM (increased to 800ms to be safe)
            await new Promise(resolve => setTimeout(resolve, 800));
        }

        try {
            // 1. Capture visual line breaks from the DOM
            // This ensures strict parity between editor (browser layout) and exported video
            const visualCaptionData = [];

            // We iterate sentences as rendered in the DOM
            const sentenceBlocks = document.querySelectorAll('.sentence-block');

            // Safety check: if for some reason DOM elements don't match content length
            if (sentenceBlocks.length !== content.length) {
                console.warn("DOM mismatch: sentence blocks count differs from content. Falling back to data-only.");
                // Fallback to existing logic if DOM is weird
            }

            content.forEach((sentence, sIdx) => {
                const block = sentenceBlocks[sIdx];
                const wordTokens = block ? block.querySelectorAll('.word-token-wrapper') : [];

                let lastTop = -1;

                sentence.words.forEach((word, wIdx) => {
                    let hasVisualBreak = false;

                    // Check visual position if DOM element exists
                    if (wIdx < wordTokens.length) {
                        const rec = wordTokens[wIdx].getBoundingClientRect();
                        // If this word is significantly lower than the previous one (and not the first word)
                        // it means a wrap occurred.
                        if (wIdx > 0 && lastTop !== -1 && rec.top > lastTop + 10) { // 10px threshold
                            hasVisualBreak = true;
                        }
                        lastTop = rec.top;
                    }

                    // Determine if we should force a break AFTER the *previous* word
                    // If this word (wIdx) started a new visual line, then wIdx-1 needs a break.
                    if (hasVisualBreak && visualCaptionData.length > 0) {
                        visualCaptionData[visualCaptionData.length - 1].line_break = true;
                    }

                    // Push current word
                    visualCaptionData.push({
                        word: word.text,
                        color: word.color || captionSettings.textColor,
                        emoji: word.emoji,
                        // If manually set, trust it. Otherwise false (until next word potentially sets it back to true)
                        line_break: word.lineBreak || false,
                        sfx: word.sfx,
                        voice_effect: word.voiceEffect
                    });
                });

                // Always break at end of sentence
                if (visualCaptionData.length > 0) {
                    visualCaptionData[visualCaptionData.length - 1].line_break = true;
                }
            });

            // Build sentence data with memes
            const sentenceData = content.map(sentence => ({
                text: sentence.text,
                meme: sentence.meme // { id, url, source, duration, offsetTop, scale } or null
            }));

            setExportProgress("Processing");

            // Auto-save project before starting render to ensure we track it
            // Only auto-save if we have a job ID (which we should)
            if (currentJobId) {
                console.log("Auto-saving project before render...");
                await handleSaveProject();
            }

            const response = await fetch(`${API_BASE}/api/rant/render`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`
                },
                body: JSON.stringify({
                    job_id: currentJobId,
                    gameplay_path: selectedGameplay.path || selectedGameplay.url,
                    caption_data: visualCaptionData, // STRONGLY typed layout
                    sentence_data: sentenceData,
                    timestamps_data: fullTimestampsData, // Pass timestamps from frontend
                    global_voice_effect: voiceEffect !== 'none' ? voiceEffect : null,
                    settings: {
                        ...captionSettings,
                        width: 1080,
                        height: 1920,
                        fps: 30
                    }
                })
            });

            const startResult = await response.json();
            if (!response.ok) throw new Error(startResult.error || 'Export failed');

            // Render started - now poll for status
            const jobId = currentJobId;
            setExportProgress("Processing");

            // Helper function to trigger actual download
            const triggerDownload = async (url, filename) => {
                setExportProgress("Processing");
                try {
                    const downloadResponse = await fetch(url);
                    const blob = await downloadResponse.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename || 'rant_video.mp4';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                } catch (downloadErr) {
                    console.error('Download failed, opening in new tab:', downloadErr);
                    window.open(url, '_blank');
                }
            };

            // Poll for status every 2 seconds (fast since it reads directly from Supabase)
            const maxAttempts = 150; // 5 minutes max (150 * 2s)
            let lastKnownStatus = 'processing';

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                // Wait 2 seconds before checking
                await new Promise(resolve => setTimeout(resolve, 2000));

                try {
                    const statusResponse = await fetch(`${API_BASE}/api/rant/status/${jobId}`, {
                        headers: {
                            'Authorization': `Bearer ${session?.access_token}`
                        }
                    });

                    // Handle non-200 responses
                    if (!statusResponse.ok) {
                        console.log(`Poll ${attempt}: HTTP ${statusResponse.status}`);
                        setExportProgress("Processing");
                        continue; // Keep polling
                    }

                    const status = await statusResponse.json();
                    lastKnownStatus = status.status;

                    // Update progress message
                    if (status.message) {
                        setExportProgress("Processing");
                    }

                    // Check if complete
                    if (status.status === 'complete' && status.download_url) {
                        setExportProgress("Download ready!");

                        // Set download URL for YouTube upload
                        setLastDownloadUrl(status.download_url);

                        if (refreshUsage) refreshUsage();

                        // Capture supabase_path for storage tracking when saving
                        if (status.supabase_path) {
                            setLastRenderedPath(status.supabase_path);
                        }

                        await triggerDownload(status.download_url, status.filename || `rant_${jobId}.mp4`);
                        return; // Success!
                    }

                    // Check for error
                    if (status.status === 'error') {
                        throw new Error(status.error || status.message || 'Render failed');
                    }

                    // Status is still processing/downloading/rendering/uploading - continue polling
                    console.log(`Poll ${attempt}: status=${status.status}`);

                } catch (pollErr) {
                    console.error(`Poll attempt ${attempt} failed:`, pollErr);
                    setExportProgress("Processing");
                    // Continue polling on network errors
                }
            }

            // Polling timed out - try recovery as last resort
            setExportProgress("Processing");
            try {
                const recoverResponse = await fetch(`${API_BASE}/api/rant/recover`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session?.access_token}`
                    },
                    body: JSON.stringify({
                        job_id: jobId,
                        min_timestamp: Math.floor(Date.now() / 1000) - 600 // Last 10 minutes
                    })
                });
                const recoverResult = await recoverResponse.json();

                if (recoverResult.found && recoverResult.download_url) {
                    setExportProgress("Download ready!");
                    if (refreshUsage) refreshUsage();
                    await triggerDownload(recoverResult.download_url, recoverResult.filename || `rant_${jobId}.mp4`);
                    return; // Success!
                }
            } catch (recoverErr) {
                console.error('Recovery failed:', recoverErr);
            }

            // All attempts failed
            throw new Error('Render is taking longer than expected. Please try again.');

        } catch (err) {
            console.error('Export error:', err.message);
            alert(`Export failed: ${err.message}`);
        } finally {
            setIsExporting(false);
            setExportProgress(null);
        }
    };

    // Get current sentence for preview
    const getCurrentSentence = () => {
        if (content.length === 0) return null;
        const idx = Math.min(currentSentenceIndex, content.length - 1);
        return content[idx] || content[0];
    };

    // Build caption style for preview
    const getCaptionStyle = () => {
        const font = FONT_OPTIONS.find(f => f.id === captionSettings.font);

        // Consistent Scaling: Scale relative to a reference 1080p width (approx 900px usable/desktop preview)
        // If preview is 360px wide, scale factor = 360/900 = 0.4.
        // If font size is 60, rendered is 24px.
        const baseWidth = 900;
        const containerScale = Math.max(0.1, previewWidth / baseWidth);

        return {
            fontFamily: font?.family || 'Lato, sans-serif',
            fontSize: `${captionSettings.fontSize * containerScale}px`,
            fontWeight: captionSettings.fontWeight === 'extra-bold' ? 900 : captionSettings.fontWeight === 'bold' ? 700 : 400,
            fontStyle: captionSettings.italic ? 'italic' : 'normal',
            textTransform: captionSettings.upperCase ? 'uppercase' : 'none',
            color: captionSettings.textColor,
            // Use Text Stroke instead of Shadow matching backend 'stroke' logic
            WebkitTextStroke: captionSettings.shadowEnabled
                ? `${captionSettings.shadowSize * 0.5}px ${captionSettings.shadowColor}`
                : '0',
            paintOrder: 'stroke fill',
            textShadow: 'none',
            backgroundColor: captionSettings.backgroundEnabled ? captionSettings.backgroundColor : 'transparent',
            padding: captionSettings.backgroundEnabled ? '8px 16px' : '0',
            borderRadius: captionSettings.backgroundEnabled ? '8px' : '0',
            maxWidth: `${captionSettings.maxWidth}%`,
            margin: '0 auto', // Center the caption box if it's narrower than container
            display: 'block', // Ensure it takes up width to center
            textAlign: 'center' // Ensure text is centered within the box
        };
    };



    // Dynamic Meme Positioning Logic (Moved here to avoid TDZ with currentSentenceIndex)
    useLayoutEffect(() => {
        const updatePlacement = () => {
            const sentence = getCurrentSentence();
            if (!sentence?.meme || !captionRef.current || !canvasWrapperRef.current) return;

            const captionRect = captionRef.current.getBoundingClientRect();
            const containerRect = canvasWrapperRef.current.getBoundingClientRect();

            if (containerRect.height === 0) return;

            // Calculate bottom of caption relative to container
            const relativeBottom = captionRect.bottom - containerRect.top;
            const bottomPercent = (relativeBottom / containerRect.height) * 100;

            // Target Top: Place below text with 2.7% padding (Reduced from 4%)
            // Ensure we never place it above 15% (header space) if text is somehow invisible/top-aligned? 
            // Actually trust the rect.
            const targetTop = bottomPercent + 2.7;

            // Calculate available height remaining in the container
            const availableHeightPercent = 96 - targetTop; // Leave 4% margin at bottom

            setMemePlacement({
                top: targetTop,
                isCentered: false, // Always anchor top edge
                // Ensure reasonable max height, but cap it if space is tight. 
                // Don't let it be negative. Minimum 10% height to be visible even if cramped.
                maxHeightPercent: Math.max(10, availableHeightPercent)
            });
        };

        // Initial update
        updatePlacement();

        // Observe caption size changes (crucial for font loading or wrapping)
        if (!captionRef.current) return;
        const resizeObserver = new ResizeObserver(updatePlacement);
        resizeObserver.observe(captionRef.current);

        return () => resizeObserver.disconnect();
    }, [currentSentenceIndex, captionSettings.paddingTop, captionSettings.fontSize, content, previewWidth]);

    return (
        <div className="rant-editor-page">
            {audioUrl && (
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                    onLoadedMetadata={(e) => { setDuration(e.target.duration); setAudioLoaded(true); }}
                    onEnded={() => { setIsPlaying(false); setCurrentSentenceIndex(0); }}
                />
            )}

            {/* Hidden SFX audio element for playing sound effects during preview */}
            <audio ref={sfxAudioRef} style={{ display: 'none' }} />



            {/* Main Content */}
            <div className={`editor-body ${isEditorExpanded ? 'expanded' : 'collapsed'}`}>
                {/* Left Sidebar */}
                {/* Left Sidebar Removed */}

                {/* Middle Panel */}
                <div className="settings-panel">
                    <div className="panel-title" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                        <button className="icon-btn header-back-btn" onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0 }}>
                            <ArrowLeft size={24} />
                        </button>
                        <div className="header-text-container">
                            <h1 style={{ margin: 0, fontSize: '24px' }}>Rants</h1>
                            <p style={{ margin: 0, fontSize: '14px', color: '#a3a3a3' }}>Turn your hot takes into viral videos</p>
                        </div>
                    </div>

                    {/* Mobile Sheet Handle */}
                    <div
                        className="sheet-handle-bar"
                        onClick={() => setIsEditorExpanded(!isEditorExpanded)}
                        onTouchStart={handleSheetTouchStart}
                        onTouchEnd={handleSheetTouchEnd}
                    >
                        <div className="sheet-handle-pill" />
                    </div>

                    <div className="panel-tabs">
                        <button className={`panel-tab ${activePanel === 'editor' ? 'active' : ''}`} onClick={() => { setActivePanel('editor'); setIsEditorExpanded(true); }}>
                            Editor
                        </button>
                        <button className={`panel-tab ${activePanel === 'settings' ? 'active' : ''}`} onClick={() => { setActivePanel('settings'); setIsEditorExpanded(true); }}>
                            Settings
                        </button>
                        <button className={`panel-tab ${activePanel === 'video' ? 'active' : ''}`} onClick={() => { setActivePanel('video'); setIsEditorExpanded(true); }}>
                            Video
                        </button>
                    </div>

                    {activePanel === 'editor' && (
                        <div className="settings-scroll editor-scroll">
                            <div className="content-section">
                                <div className="content-header">
                                    <h3>Content</h3>
                                    <button className="edit-script-btn" onClick={onBack}>
                                        <ArrowLeft size={14} /> Edit Script
                                    </button>
                                </div>
                                <p className="content-hint">Click words to change colors or add emojis</p>

                                <div className="sentences-editor">
                                    {content.length === 0 ? (
                                        <div className="no-content"><p>No script content</p></div>
                                    ) : (
                                        content.map((sentence, sIdx) => (
                                            <div key={sIdx} className={`sentence-block ${currentSentenceIndex === sIdx ? 'current' : ''}`}>
                                                <div className="sentence-number">{sIdx + 1}</div>
                                                <div className="words-row">
                                                    {sentence.words.map((word, wIdx) => (
                                                        <React.Fragment key={wIdx}>
                                                            <WordToken
                                                                word={word}
                                                                onColorChange={(color) => updateWord(sIdx, wIdx, { color })}
                                                                onEmojiChange={(emoji) => updateWord(sIdx, wIdx, { emoji })}
                                                                onLineBreakToggle={() => updateWord(sIdx, wIdx, { lineBreak: !word.lineBreak })}
                                                                onSfxChange={(sfx) => updateWord(sIdx, wIdx, { sfx })}
                                                                onSfxClick={() => setSfxPickerOpen({ sentenceIdx: sIdx, wordIdx: wIdx, currentSfx: word.sfx })}
                                                                onVoiceEffectChange={(voiceEffect) => updateWord(sIdx, wIdx, { voiceEffect })}
                                                                sfxLibrary={sfxLibrary}
                                                            />
                                                            {word.lineBreak && <div className="line-break-spacer" />}
                                                        </React.Fragment>
                                                    ))}

                                                    {/* Meme thumbnail or Add Hook button */}
                                                    {sentence.meme ? (
                                                        <div className="sentence-meme-container">
                                                            <img
                                                                src={sentence.meme.url}
                                                                alt="meme"
                                                                className="sentence-meme-thumb"
                                                                onClick={() => setMemePropertiesOpen({ sentenceIdx: sIdx })}
                                                            />
                                                            <button
                                                                className="remove-meme-btn"
                                                                onClick={() => handleRemoveMeme(sIdx)}
                                                                title="Remove meme"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className="add-hook-btn"
                                                            onClick={() => handleAddHook(sIdx)}
                                                            title="Add meme/hook"
                                                        >
                                                            <ImageIcon size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activePanel === 'settings' && (
                        <div className="settings-scroll">
                            {/* Captions - Matching Screenshot */}
                            <CollapsibleSection title="Captions" icon={Type} defaultOpen={false}>
                                {/* Font & Size Row */}
                                <div className="setting-row">
                                    <div className="setting-group flex-2">
                                        <div className="select-wrapper">
                                            <select
                                                value={captionSettings.font}
                                                onChange={(e) => setCaptionSettings({ ...captionSettings, font: e.target.value })}
                                                className="font-select"
                                            >
                                                {FONT_OPTIONS.map(f => (
                                                    <option key={f.id} value={f.id}>{f.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="setting-group flex-1">
                                        <input
                                            type="number"
                                            value={captionSettings.fontSize}
                                            onChange={(e) => setCaptionSettings({ ...captionSettings, fontSize: parseInt(e.target.value) || 75 })}
                                            className="size-input"
                                            min={20}
                                            max={200}
                                        />
                                    </div>
                                    <div className="setting-group">
                                        <button
                                            className={`toggle-pill ${captionSettings.upperCase ? 'active' : ''}`}
                                            onClick={() => setCaptionSettings({ ...captionSettings, upperCase: !captionSettings.upperCase })}
                                        >
                                            Upper case
                                        </button>
                                    </div>
                                </div>

                                {/* Text Style Row */}
                                <div className="setting-row">
                                    <div className="style-buttons">
                                        <button
                                            className={`style-btn ${captionSettings.fontWeight === 'extra-bold' ? 'active' : ''}`}
                                            onClick={() => setCaptionSettings({ ...captionSettings, fontWeight: 'extra-bold' })}
                                        >
                                            <span className="extra-bold-text">Extra</span> <Bold size={14} />
                                        </button>
                                        <button
                                            className={`style-btn ${captionSettings.fontWeight === 'bold' ? 'active' : ''}`}
                                            onClick={() => setCaptionSettings({ ...captionSettings, fontWeight: 'bold' })}
                                        >
                                            <Bold size={16} />
                                        </button>
                                        <button
                                            className={`style-btn ${captionSettings.italic ? 'active' : ''}`}
                                            onClick={() => setCaptionSettings({ ...captionSettings, italic: !captionSettings.italic })}
                                        >
                                            <Italic size={16} />
                                        </button>
                                    </div>
                                    <div className="color-picker-row">
                                        <span>Text color</span>
                                        <input
                                            type="color"
                                            value={captionSettings.textColor}
                                            onChange={(e) => setCaptionSettings({ ...captionSettings, textColor: e.target.value })}
                                            className="color-input"
                                        />
                                    </div>
                                </div>

                                {/* Stroke Row */}
                                <div className="setting-row">
                                    <button
                                        className={`toggle-pill ${captionSettings.shadowEnabled ? 'active' : ''}`}
                                        onClick={() => setCaptionSettings({ ...captionSettings, shadowEnabled: !captionSettings.shadowEnabled })}
                                    >
                                        Stroke
                                    </button>
                                    {captionSettings.shadowEnabled && (
                                        <>
                                            <input
                                                type="color"
                                                value={captionSettings.shadowColor}
                                                onChange={(e) => setCaptionSettings({ ...captionSettings, shadowColor: e.target.value })}
                                                className="color-input shadow-color"
                                            />
                                            <span className="size-label">Size</span>
                                            <input
                                                type="number"
                                                value={captionSettings.shadowSize}
                                                onChange={(e) => setCaptionSettings({ ...captionSettings, shadowSize: parseInt(e.target.value) || 5 })}
                                                className="size-input small"
                                                min={0}
                                                max={20}
                                            />
                                        </>
                                    )}
                                </div>

                                {/* Background Row */}
                                <div className="setting-row">
                                    <button
                                        className={`toggle-pill ${captionSettings.backgroundEnabled ? 'active' : ''}`}
                                        onClick={() => setCaptionSettings({ ...captionSettings, backgroundEnabled: !captionSettings.backgroundEnabled })}
                                    >
                                        Background
                                    </button>
                                </div>

                                {/* Padding Top */}
                                <div className="setting-group">
                                    <label>Vertical Position ({captionSettings.paddingTop}%)</label>
                                    <input
                                        type="range"
                                        className="slider-input"
                                        min="0"
                                        max="90"
                                        value={captionSettings.paddingTop}
                                        onChange={(e) => setCaptionSettings({ ...captionSettings, paddingTop: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div className="setting-group">
                                    <label>Max Width ({captionSettings.maxWidth}%)</label>
                                    <input
                                        type="range"
                                        className="slider-input"
                                        min="30"
                                        max="100"
                                        value={captionSettings.maxWidth || 80}
                                        onChange={(e) => setCaptionSettings({ ...captionSettings, maxWidth: parseInt(e.target.value) })}
                                    />
                                </div>

                            </CollapsibleSection>


                            {/* Gameplay and Music Selection */}
                            <CollapsibleSection title="Gameplay and Music" icon={Gamepad2} defaultOpen={false}>
                                <div className="asset-picker-integrated">
                                    {assetsLoading ? (
                                        <div className="loading">Loading...</div>
                                    ) : (
                                        <div className="asset-grid">
                                            {gameplayLibrary.map(gp => (
                                                <div
                                                    key={gp.id}
                                                    className={`asset-thumb ${selectedGameplay?.id === gp.id ? 'selected' : ''}`}
                                                    onClick={() => setSelectedGameplay(gp)}
                                                    onMouseEnter={(e) => {
                                                        const vid = e.currentTarget.querySelector('video');
                                                        if (vid) {
                                                            vid.preload = "auto";
                                                            vid.play().catch(() => { });
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const vid = e.currentTarget.querySelector('video');
                                                        if (vid) {
                                                            vid.pause();
                                                            vid.currentTime = 0;
                                                            vid.preload = "none";
                                                        }
                                                    }}
                                                >
                                                    <video
                                                        src={gp.url}
                                                        poster={gp.thumbnail_url}
                                                        muted
                                                        playsInline
                                                        loop
                                                        preload="metadata"
                                                        className="thumb-video"
                                                    />
                                                    <div className="thumb-overlay">
                                                        <span>{gp.label}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {gameplayLibrary.length === 0 && (
                                                <p className="no-assets">No gameplay videos<br />Add to backend/downloads/gameplay/</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="setting-group" style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                                    <div className="coming-soon-notice" style={{ margin: 0 }}>
                                        <h4>Background Music</h4>
                                        <p>Select a track to play in the background</p>
                                        <span className="coming-soon-badge">Coming Soon</span>
                                    </div>
                                </div>
                            </CollapsibleSection>

                            {/* Voice Changer */}
                            <CollapsibleSection title="Voice changer" icon={Wand2} defaultOpen={false}>
                                {/* New Voice Selection */}
                                <div className="setting-group">
                                    <label>
                                        Voice
                                        <span className="credit-tooltip" title="Applying a new voice uses 10 credits">
                                            ℹ️ 10 credits
                                        </span>
                                    </label>
                                    <div className="voice-selector-row">
                                        <div className="select-wrapper" style={{ flex: 1 }}>
                                            <select value={newVoice} onChange={(e) => setNewVoice(e.target.value)} disabled={voicesLoading}>
                                                {voicesLoading ? (
                                                    <option>Loading voices...</option>
                                                ) : (
                                                    <>
                                                        <optgroup label="Standard Voices">
                                                            {availableVoices.filter(v => !v.is_cloned).map(v => (
                                                                <option key={v.id} value={v.id}>{v.label}</option>
                                                            ))}
                                                        </optgroup>
                                                        {availableVoices.some(v => v.is_cloned) && (
                                                            <optgroup label="My Cloned Voices">
                                                                {availableVoices.filter(v => v.is_cloned).map(v => (
                                                                    <option key={v.id} value={v.id}>{v.label} {v.is_default ? '(Default)' : ''}</option>
                                                                ))}
                                                            </optgroup>
                                                        )}
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                        <VoicePreviewButton voiceId={newVoice} voiceList={availableVoices} />
                                    </div>
                                    <button
                                        className="apply-voice-btn"
                                        onClick={async () => {
                                            if (!currentJobId || !initialSentences?.length) return;

                                            setApplyingNewVoice(true);
                                            try {
                                                // Update all sentences to use the new voice
                                                const updatedSentences = initialSentences.map(s => ({
                                                    ...s,
                                                    voice: newVoice
                                                }));

                                                // Generate a unique session ID for idempotency
                                                const voiceSessionId = crypto.randomUUID ? crypto.randomUUID() :
                                                    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                                                const response = await fetch(`${API_BASE}/api/rant/generate-voice`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'Authorization': `Bearer ${session?.access_token}`
                                                    },
                                                    body: JSON.stringify({
                                                        sentences: updatedSentences,
                                                        voice_session_id: voiceSessionId,
                                                        regenerate: true // Flag indicating this is a regeneration
                                                    })
                                                });

                                                const result = await response.json();
                                                if (!response.ok) throw new Error(result.error || 'Failed to apply voice');

                                                // Refresh the audio URL
                                                if (result.job_id) {
                                                    setCurrentJobId(result.job_id);
                                                    setAudioCacheBuster(prev => prev + 1);
                                                    if (refreshUsage) refreshUsage();
                                                    if (refreshUsage) refreshUsage();
                                                    const voiceLabel = availableVoices.find(v => v.id === newVoice)?.label || newVoice;
                                                    alert(`Voice changed to ${voiceLabel}!`);
                                                }
                                            } catch (err) {
                                                console.error('Apply voice error:', err);
                                                alert(`Failed to apply voice: ${err.message}`);
                                            } finally {
                                                setApplyingNewVoice(false);
                                            }
                                        }}
                                        disabled={applyingNewVoice || !currentJobId}
                                    >
                                        {applyingNewVoice ? (
                                            <><Loader2 size={14} className="spin" /> Applying...</>
                                        ) : (
                                            'Apply Voice'
                                        )}
                                    </button>
                                </div>

                                {/* Voice Effect */}
                                <div className="setting-group" style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                                    <label>Effect</label>
                                    <div className="select-wrapper">
                                        <select value={voiceEffect} onChange={(e) => setVoiceEffect(e.target.value)}>
                                            {VOICE_EFFECTS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                {voiceEffect !== 'none' && (
                                    <button
                                        className="apply-effect-btn"
                                        onClick={handleApplyVoiceEffect}
                                        disabled={applyingVoiceEffect || !currentJobId}
                                    >
                                        {applyingVoiceEffect ? (
                                            <><Loader2 size={14} className="spin" /> Applying...</>
                                        ) : (
                                            'Apply Effect'
                                        )}
                                    </button>
                                )}
                            </CollapsibleSection>






                        </div>
                    )}

                    {activePanel === 'video' && (
                        <div className="video-settings">
                            <h3>Video Settings</h3>
                            <div className="setting-group">
                                <label>Resolution</label>
                                <div className="select-wrapper">
                                    <select defaultValue="1080x1920">
                                        <option value="1080x1920">1080 x 1920 (Full HD)</option>
                                        <option value="720x1280">720 x 1280 (HD)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="setting-group">
                                <label>Frame Rate</label>
                                <div className="select-wrapper">
                                    <select defaultValue="30">
                                        <option value="30">30 FPS</option>
                                        <option value="60">60 FPS</option>
                                    </select>
                                </div>
                            </div>
                            <div className="audio-info">
                                <h4>Audio</h4>
                                {audioLoaded ? (
                                    <p className="success">✓ Audio loaded ({formatTime(duration)})</p>
                                ) : (
                                    <p className="loading">Loading audio...</p>
                                )}
                            </div>

                            {/* Mobile Actions - Moved from Footer (only visible on mobile via CSS) */}
                            <div className="mobile-video-actions">
                                <button
                                    className="download-btn-large"
                                    onClick={handleExport}
                                    disabled={isExporting || !selectedGameplay}
                                >
                                    {isExporting ? (
                                        <><Loader2 size={18} className="spin" /> {exportProgress}</>
                                    ) : (
                                        <><Download size={18} /> Download Video</>
                                    )}
                                </button>

                                <div className="save-row">
                                    <button
                                        className="save-btn"
                                        onClick={handleSaveProject}
                                        disabled={saveStatus !== 'idle'}
                                    >
                                        {saveStatus === 'saving' ? <Loader2 size={18} className="spin" /> : saveStatus === 'saved' ? <Check size={18} /> : <Save size={18} />}
                                        {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Project'}
                                    </button>

                                    <button
                                        className="save-btn"
                                        onClick={() => setShowYoutubeModal(true)}
                                        disabled={!lastRenderedPath && !lastDownloadUrl}
                                        title={lastRenderedPath || lastDownloadUrl ? "Upload to YouTube" : "Render video first"}
                                    >
                                        <Youtube size={18} color={lastRenderedPath ? "#FF0000" : "currentColor"} />
                                        Upload to YT
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Global Footer (Visible on all tabs) */}
                    <div className="settings-footer" style={{ display: 'flex', gap: '12px', padding: '24px 32px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>                        <button
                        className="btn-secondary save-btn"
                        onClick={handleSaveProject}
                        disabled={saveStatus !== 'idle'}
                        title={saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Project'}
                        style={{
                            width: '48px',
                            flex: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#262626',
                            border: '1px solid #404040',
                            color: '#fff',
                            cursor: 'pointer'
                        }}
                    >
                        {saveStatus === 'saving' ? <Loader2 size={24} className="spin" /> : saveStatus === 'saved' ? <Check size={24} /> : <Save size={24} />}
                    </button>
                        <button
                            className="download-btn-large"
                            onClick={handleExport}
                            disabled={isExporting || !selectedGameplay}
                            style={{ flex: 1 }}
                        >
                            {isExporting ? (
                                <><Loader2 size={18} className="spin" /> {exportProgress}</>
                            ) : (
                                <><Download size={18} /> Download Video</>
                            )}
                        </button>

                        <button
                            className="btn-secondary save-btn"
                            onClick={() => setShowYoutubeModal(true)}
                            disabled={!lastRenderedPath && !lastDownloadUrl}
                            title={lastRenderedPath || lastDownloadUrl ? "Upload to YouTube" : "Render video first"}
                            style={{
                                width: '48px',
                                flex: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0',
                                height: '48px',
                                borderRadius: '12px',
                                background: '#262626',
                                border: '1px solid #404040',
                                color: '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            <Youtube size={24} color={lastRenderedPath ? "#FF0000" : "currentColor"} />
                        </button>
                    </div>
                </div>

                {/* Right Panel - Preview */}
                <div className="preview-panel">
                    <div className="preview-container-centered">
                        <div className="canvas-wrapper" ref={canvasWrapperRef}>
                            {/* Gameplay Background */}
                            <div className="preview-background">
                                {selectedGameplay ? (
                                    <video ref={gameplayVideoRef} src={selectedGameplay.url} loop muted playsInline className="gameplay-video" />
                                ) : (
                                    <div className="placeholder-bg">
                                        <Gamepad2 size={48} opacity={0.3} />
                                        <p>Select gameplay</p>
                                    </div>
                                )}
                            </div>

                            {/* Meme Overlay - Shows when current sentence has a meme AND within time range */}
                            {(() => {
                                const sentence = getCurrentSentence();
                                const sentenceIdx = currentSentenceIndex;
                                const sentenceTs = sentenceTimestamps[sentenceIdx];

                                if (!sentence?.meme || !sentenceTs) return null;

                                // Calculate time within this sentence (in ms)
                                const tempoFactor = getTempoMultiplier();
                                // Scale timestamps: relative time needs to be adjusted because audio is faster/slower
                                // If tempo=1.15 (faster), start/end times happen earlier (divide by tempo)
                                const scaledStart = sentenceTs.start / tempoFactor;
                                const timeInSentence = (currentTime - scaledStart) * 1000;

                                // Check if meme should be visible based on timeline range
                                const memeStart = sentence.meme.startTime ?? 0;
                                const memeEnd = sentence.meme.endTime ?? (sentenceTs.end - sentenceTs.start) * 1000;
                                const shouldShowMeme = timeInSentence >= memeStart && timeInSentence <= memeEnd;

                                if (!shouldShowMeme) return null;

                                return (
                                    <div
                                        className="meme-overlay"
                                        style={{
                                            left: '50%',
                                            top: memePlacement.isCentered ? `${50 + (sentence.meme.offsetTop || 0)}%` : `${memePlacement.top + (sentence.meme.offsetTop || 0)}%`,
                                            transform: memePlacement.isCentered
                                                ? `translate(-50%, -50%) scale(${sentence.meme.scale || 1})`
                                                : `translate(-50%, 0) scale(${sentence.meme.scale || 1})`,
                                            width: '100%', // Ensure centering works
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <img
                                            src={sentence.meme.url}
                                            alt="meme overlay"
                                            className="meme-overlay-image"
                                            style={{
                                                maxHeight: memePlacement.maxHeightPercent ? `${memePlacement.maxHeightPercent}%` : '85%'
                                            }}
                                        />
                                    </div>
                                );
                            })()}

                            {/* Caption Preview - ONE SENTENCE AT A TIME */}
                            <div
                                className="caption-preview"
                                style={{ top: `${captionSettings.paddingTop}%` }}
                            >
                                {getCurrentSentence() && (
                                    <div className="caption-sentence" ref={captionRef} style={getCaptionStyle()}>
                                        {getCurrentSentence().words.map((word, i) => (
                                            <React.Fragment key={i}>
                                                <span style={{ color: word.color || captionSettings.textColor }}>
                                                    {word.text}
                                                </span>
                                                {word.emoji && <span className="caption-emoji">{word.emoji}</span>}
                                                {word.lineBreak ? <br /> : ' '}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Playback Controls (Matching Video Ranking Style) */}
                        <div className="playback-controls">
                            <div className="control-buttons">
                                <button
                                    className="control-btn"
                                    onClick={() => {
                                        if (audioRef.current) audioRef.current.currentTime = 0;
                                        setCurrentTime(0);
                                    }}
                                    title="Reset"
                                >
                                    <RotateCcw size={18} />
                                </button>

                                <button
                                    className="control-btn play-btn"
                                    onClick={togglePlayback}
                                    title={isPlaying ? 'Pause' : 'Play'}
                                    disabled={!audioLoaded}
                                >
                                    {isBuffering ? (
                                        <Loader2 size={20} className="spin" />
                                    ) : isPlaying ? (
                                        <Pause size={20} />
                                    ) : (
                                        <Play size={20} fill="currentColor" />
                                    )}
                                </button>

                                <button
                                    className="control-btn"
                                    onClick={() => {
                                        // Handle mute if needed, for now just ui
                                    }}
                                    title="Mute"
                                >
                                    {false ? <VolumeX size={18} /> : <Volume2 size={18} />}
                                </button>
                            </div>

                            <div className="progress-container">
                                <input
                                    type="range"
                                    min="0"
                                    max={duration || 100}
                                    value={currentTime}
                                    onChange={(e) => {
                                        if (audioRef.current) audioRef.current.currentTime = e.target.value;
                                        setCurrentTime(parseFloat(e.target.value));
                                    }}
                                    className="progress-slider-range"
                                />
                                <div className="progress-bar-visual">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>

                            <div className="time-display">
                                <span className="current-time">{formatTime(currentTime)}</span>
                                <span className="time-separator">/</span>
                                <span className="total-time">{formatTime(duration)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Asset Selection */}
                </div>
            </div>


            {/* Meme Picker Modal */}
            < MemePickerModal
                isOpen={memePickerOpen}
                onClose={() => {
                    setMemePickerOpen(false);
                    setSelectedSentenceForMeme(null);
                }}
                onSelect={handleMemeSelected}
                memesLibrary={memesLibrary}
                session={session}
                contextText={selectedSentenceForMeme !== null ? content[selectedSentenceForMeme]?.words.map(w => w.text).join(' ') : ''}
                previousContext={selectedSentenceForMeme !== null && selectedSentenceForMeme > 0 ? content[selectedSentenceForMeme - 1]?.words.map(w => w.text).join(' ') : ''}
            />

            {/* Meme Properties Editor */}
            {
                memePropertiesOpen && content[memePropertiesOpen.sentenceIdx]?.meme && (
                    <div className="meme-properties-overlay" onClick={() => setMemePropertiesOpen(null)}>
                        <div onClick={e => e.stopPropagation()}>
                            <MemePropertiesEditor
                                meme={content[memePropertiesOpen.sentenceIdx].meme}
                                onUpdate={(updatedMeme) => {
                                    updateSentence(memePropertiesOpen.sentenceIdx, { meme: updatedMeme });
                                }}
                                onClose={() => setMemePropertiesOpen(null)}
                                sentenceDuration={
                                    sentenceTimestamps[memePropertiesOpen.sentenceIdx]
                                        ? (sentenceTimestamps[memePropertiesOpen.sentenceIdx].end -
                                            sentenceTimestamps[memePropertiesOpen.sentenceIdx].start) * 1000
                                        : 3000
                                }
                            />
                        </div>
                    </div>
                )
            }

            {/* SFX Picker Modal */}
            <SfxPickerModal
                isOpen={sfxPickerOpen !== null}
                onClose={() => setSfxPickerOpen(null)}
                onSelect={(sfx) => {
                    if (sfxPickerOpen) {
                        updateWord(sfxPickerOpen.sentenceIdx, sfxPickerOpen.wordIdx, { sfx });
                    }
                    setSfxPickerOpen(null);
                }}
                sfxLibrary={sfxLibrary}
                currentSfx={sfxPickerOpen?.currentSfx}
            />

            <YouTubeUploadModal
                isOpen={showYoutubeModal}
                onClose={() => setShowYoutubeModal(false)}
                session={session}
                project={{
                    id: projectId || `rant_${currentJobId}`,
                    name: title || 'Rant Video',
                    output_url: lastDownloadUrl,
                    data: {
                        script: initialSentences?.map(s => s.text).join(' ') || '',
                        supabase_path: lastRenderedPath
                    }
                }}
            />
        </div >
    );
};

function formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default RantEditorPage;
